import { type SeededRuntime, wait } from "@/lib/seeded";
import {
  addCampaign as addCampaignAction,
  addCoupon as addCouponAction,
  approveBrand as approveBrandAction,
  assignLead as assignLeadAction,
  brokerWithdraw as brokerWithdrawAction,
  closeTicket as closeTicketAction,
  closeUpsell as closeUpsellAction,
  deleteProduct as deleteProductAction,
  payDividend as payDividendAction,
  placeOrder as placeOrderAction,
  rejectLead as rejectLeadAction,
  replyTicket as replyTicketAction,
  sendPush as sendPushAction,
  supplierWithdraw as supplierWithdrawAction,
  toggleBrandStatus as toggleBrandStatusAction,
  upsertProduct as upsertProductAction,
  YJDomainError,
  type ActionResult,
} from "./actions";
import type { YJMockData } from "./seed";
import type {
  Brand,
  BrandId,
  BrokerId,
  Campaign,
  Coupon,
  Customer,
  CustomerId,
  Lead,
  LeadId,
  Order,
  PaymentMethod,
  Product,
  ProductId,
  Settlement,
  TicketId,
} from "./types";

export interface YJApi {
  // Customer
  placeOrder(input: {
    customerId?: CustomerId;
    productId: ProductId;
    brokerId?: BrokerId;
    paymentMethod: PaymentMethod;
    customer?: Omit<Customer, "id">;
  }): Promise<{ order: Order; lead: Lead }>;

  // Telesales
  assignLead(leadId: LeadId, agent: string): Promise<Lead>;
  closeUpsell(input: { leadId: LeadId; productId: ProductId; useHouseBrand: boolean; discount: number }): Promise<Order>;
  rejectLead(leadId: LeadId, reason: string): Promise<Lead>;

  // Supplier
  upsertProduct(p: Partial<Product> & { brandId: BrandId; name: string; price: number }): Promise<Product>;
  deleteProduct(id: ProductId): Promise<void>;
  supplierWithdraw(brandId: BrandId, amount: number): Promise<void>;

  // Broker
  brokerWithdraw(brokerId: BrokerId, amount: number): Promise<void>;

  // Admin
  approveBrand(id: BrandId): Promise<Brand>;
  toggleBrandStatus(id: BrandId): Promise<Brand>;
  addCoupon(c: Omit<Coupon, "id" | "used" | "status" | "createdAt">): Promise<Coupon>;
  addCampaign(c: Omit<Campaign, "id" | "spent" | "gmv" | "status" | "startsAt">): Promise<Campaign>;
  sendPush(p: { title: string; body: string }): Promise<void>;
  replyTicket(id: TicketId, text: string): Promise<void>;
  closeTicket(id: TicketId): Promise<void>;
  payDividend(settlementId: string): Promise<Settlement>;
}

export type YJApiDeps = {
  getState: () => YJMockData;
  setState: (state: YJMockData) => void;
  runtime: SeededRuntime;
};

function applyResult<T>(deps: YJApiDeps, result: ActionResult<T>) {
  deps.setState(result.state);
  return result.data;
}

export function createYJApi(deps: YJApiDeps): YJApi {
  let placeOrderCalls = 0;

  const mutate = async <T>(fn: (state: YJMockData) => ActionResult<T>) => {
    await wait(deps.runtime.latencyMs());
    return applyResult(deps, fn(deps.getState()));
  };

  return {
    async placeOrder(input) {
      await wait(deps.runtime.latencyMs());
      const state = deps.getState();
      placeOrderCalls += 1;
      if (state.scenarioConfig.paymentFailEvery && placeOrderCalls % state.scenarioConfig.paymentFailEvery === 0) {
        throw new YJDomainError("จำลองการชำระเงินล้มเหลว", "PAYMENT_FAILED");
      }
      return applyResult(deps, placeOrderAction(state, input));
    },
    assignLead(leadId, agent) {
      return mutate((state) => assignLeadAction(state, leadId, agent));
    },
    closeUpsell(input) {
      return mutate((state) => closeUpsellAction(state, input));
    },
    rejectLead(leadId, reason) {
      return mutate((state) => rejectLeadAction(state, leadId, reason));
    },
    upsertProduct(input) {
      return mutate((state) => upsertProductAction(state, input));
    },
    deleteProduct(id) {
      return mutate((state) => deleteProductAction(state, id));
    },
    supplierWithdraw(brandId, amount) {
      return mutate((state) => supplierWithdrawAction(state, brandId, amount));
    },
    brokerWithdraw(brokerId, amount) {
      return mutate((state) => brokerWithdrawAction(state, brokerId, amount));
    },
    approveBrand(id) {
      return mutate((state) => approveBrandAction(state, id));
    },
    toggleBrandStatus(id) {
      return mutate((state) => toggleBrandStatusAction(state, id));
    },
    addCoupon(input) {
      return mutate((state) => addCouponAction(state, input));
    },
    addCampaign(input) {
      return mutate((state) => addCampaignAction(state, input));
    },
    sendPush(input) {
      return mutate((state) => sendPushAction(state, input));
    },
    replyTicket(id, text) {
      return mutate((state) => replyTicketAction(state, id, text));
    },
    closeTicket(id) {
      return mutate((state) => closeTicketAction(state, id));
    },
    payDividend(settlementId) {
      return mutate((state) => payDividendAction(state, settlementId));
    },
  };
}
